import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.jsx";
import { api } from "../api/client.js";
import { useLoading } from "../loading/LoadingContext.jsx";

const emptyForm = {
  account_id: "",
  account_name: "",
  role_arn: "",
  external_id: "",
  org_role: "standalone",
  parent_account_id: "",
  monthly_budget: "",
};

const ORG_ROLE_BADGE = { master: "badge-master", standalone: "badge-standalone", member: "badge-member" };

function OrgRoleBadge({ role }) {
  return <span className={`badge ${ORG_ROLE_BADGE[role] || "badge-standalone"}`}>{role}</span>;
}

function formatBudget(value) {
  if (value === null || value === undefined || value === "") return null;
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * Inline monthly-budget editor.
 *
 * Editing lives here rather than behind a full re-registration because a
 * budget is the one field on an account that genuinely changes — and
 * re-registering would demand the Role ARN and ExternalId again just to
 * change a number. An empty field clears the budget; that is not the same as
 * entering 0, which is a real budget of zero and shows every dollar as an
 * overrun.
 */
function BudgetCell({ account, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(account.monthly_budget ?? "");
  const [saving, setSaving] = useState(false);

  if (!editing) {
    return (
      <>
        {formatBudget(account.monthly_budget) || <span className="muted">Not set</span>}{" "}
        <button
          type="button"
          className="link"
          onClick={() => {
            setValue(account.monthly_budget ?? "");
            setEditing(true);
          }}
        >
          edit
        </button>
      </>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await onSave(account.account_id, value === "" ? null : Number(value));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <input
        type="number"
        min="0"
        step="any"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="none"
        style={{ width: 110 }}
        aria-label={`Monthly budget for ${account.account_name}`}
      />
      <button type="button" className="link" onClick={save} disabled={saving}>
        {saving ? "…" : "save"}
      </button>
      <button type="button" className="link" onClick={() => setEditing(false)} disabled={saving}>
        cancel
      </button>
    </span>
  );
}

export default function Accounts() {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [errorMsg, setErrorMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    start();
    api
      .getAccounts(idToken)
      .then((r) => setAccounts(r.accounts))
      .catch((err) => setErrorMsg(err.message))
      .finally(stop);
  };

  useEffect(load, [idToken]);

  // Accounts a "member" registration can point parent_account_id at — any
  // already-registered master/standalone account (it needs its own
  // BillingDashboardReadRole deployed, which members don't have).
  const possibleParents = accounts.filter((a) => a.org_role !== "member");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg(null);
    start();
    try {
      await api.createAccount(idToken, {
        ...form,
        monthly_budget: form.monthly_budget === "" ? null : Number(form.monthly_budget),
      });
      setForm(emptyForm);
      load();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSubmitting(false);
      stop();
    }
  };

  const onSaveBudget = async (accountId, monthlyBudget) => {
    setErrorMsg(null);
    start();
    try {
      await api.updateAccount(idToken, accountId, { monthly_budget: monthlyBudget });
      load();
    } catch (err) {
      setErrorMsg(err.message);
      throw err;
    } finally {
      stop();
    }
  };

  const onDelete = async (accountId) => {
    if (!confirm(`Remove account ${accountId} from the dashboard? This does not delete the AWS account or its role.`)) return;
    start();
    try {
      await api.deleteAccount(idToken, accountId);
      load();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      stop();
    }
  };

  return (
    <div>
      <div className="topbar">
        <h2>Billing accounts</h2>
      </div>
      <div className="page-hint">
        <ul>
          <li>Only members of the <code>Admins</code> group can register, edit or remove accounts.</li>
          <li>
            <strong>Payer (master) or standalone account:</strong> deploy the read-only role from{" "}
            <code>cross-account-role/template.yaml</code> first, then enter its Role ARN and External ID.
          </li>
          <li>
            <strong>Member account:</strong> no role needed. Choose its payer account, and its costs are read from the
            payer's Cost Explorer.
          </li>
        </ul>
      </div>
      {errorMsg && <div className="error-banner">{errorMsg}</div>}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Account ID</th>
              <th>Org role</th>
              <th>Parent (if member)</th>
              <th>Monthly budget</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-row">
                  No accounts registered yet — add one below.
                </td>
              </tr>
            )}
            {accounts.map((a) => (
              <tr key={a.account_id}>
                <td>{a.account_name}</td>
                <td className="muted mono">{a.account_id}</td>
                <td>
                  <OrgRoleBadge role={a.org_role} />
                </td>
                <td className="muted mono">{a.parent_account_id || "—"}</td>
                <td>
                  <BudgetCell account={a} onSave={onSaveBudget} />
                </td>
                <td style={{ textAlign: "right" }}>
                  <button className="danger-link" onClick={() => onDelete(a.account_id)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ maxWidth: 480 }}>
        <h3>Register a new account</h3>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label>AWS Account ID</label>
            <input
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              placeholder="123456789012"
              required
            />
          </div>
          <div className="field">
            <label>Display name</label>
            <input
              value={form.account_name}
              onChange={(e) => setForm({ ...form, account_name: e.target.value })}
              placeholder="e.g. Production"
              required
            />
          </div>
          <div className="field">
            <label>Org role</label>
            <select value={form.org_role} onChange={(e) => setForm({ ...form, org_role: e.target.value, parent_account_id: "" })}>
              <option value="master">master (payer account)</option>
              <option value="member">member (of some other registered payer's Organization)</option>
              <option value="standalone">standalone</option>
            </select>
          </div>
          {form.org_role === "member" ? (
            <>
              <div className="field">
                <label>Parent (payer) account</label>
                {possibleParents.length === 0 ? (
                  <p className="error-text" style={{ marginTop: -2 }}>
                    No master/standalone accounts are registered yet — register this account's payer first (with its
                    Role ARN/ExternalId), then come back and register this member.
                  </p>
                ) : (
                  <select
                    value={form.parent_account_id}
                    onChange={(e) => setForm({ ...form, parent_account_id: e.target.value })}
                    required
                  >
                    <option value="" disabled>
                      Select the payer this account is consolidated under…
                    </option>
                    {possibleParents.map((p) => (
                      <option key={p.account_id} value={p.account_id}>
                        {p.account_name} ({p.account_id})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <p className="field-hint">
                No Role ARN or External ID needed. Costs are read through the payer account's role, filtered to this
                account.
              </p>
            </>
          ) : (
            <>
              <div className="field">
                <label>Role ARN</label>
                <input
                  value={form.role_arn}
                  onChange={(e) => setForm({ ...form, role_arn: e.target.value })}
                  placeholder="arn:aws:iam::123456789012:role/BillingDashboardReadRole"
                  required
                />
              </div>
              <div className="field">
                <label>External ID</label>
                <input
                  value={form.external_id}
                  onChange={(e) => setForm({ ...form, external_id: e.target.value })}
                  required
                />
              </div>
            </>
          )}
          <div className="field">
            <label>Monthly budget (USD, optional)</label>
            <input
              type="number"
              min="0"
              step="any"
              value={form.monthly_budget}
              onChange={(e) => setForm({ ...form, monthly_budget: e.target.value })}
              placeholder="e.g. 25000"
            />
          </div>
          <p className="field-hint">
            Sets the over/under budget bar on the Spend page. Leave blank for no budget; you can change it later in
            the table above.
          </p>
          <button className="primary" type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Register account"}
          </button>
        </form>
      </div>
    </div>
  );
}
